import { makeUTCDate } from "@/services/assets/parsers";
import type { IPostgrestParams, IWebCOOSApiRequestParams } from "./types";

export const postgrestEndpoint = <T,>({
    apiUrl = import.meta.env.VITE_WEBCOOS_API_URL,
    apiVersion = 'v1',
    source = 'webcoos',
    params
}: Omit<IWebCOOSApiRequestParams, 'token' | 'signal'> & {
    params: IPostgrestParams<T>
}): string => {
    const url = new URL(`${apiUrl}/${apiVersion}/${source}/postgrest/${params.table}`);
    (params.filters ?? []).forEach(f => url.searchParams.append(String(f.column), `${f.not ? 'not.' : ''}${f.operator ?? 'eq'}.${f.value}`));
    if (params.limit !== undefined) {
        url.searchParams.append('limit', params.limit.toString());
    }
    if (params.offset !== undefined) {
        url.searchParams.append('offset', params.offset.toString());
    }
    if (params.order !== undefined && params.order.length > 0) {
        url.searchParams.append('order', params.order.map(o => `${String(o.column)}.${String(o.dir)}`).join(','));
    }
    if (params.select !== undefined) {
        const select = params.select.map(s => {
            const o = typeof s === 'string' ? { column: s } : s;
            return `${o.as ? `${o.as}:` : ''}${String(o.column)}${o.fn !== undefined ? `.${o.fn}()` : ''}`
        }).join(',');
        url.searchParams.append('select', select);
    }
    return url.toString();
}

export const firstAssetMediaEndpoint = ({
    apiUrl = import.meta.env.VITE_WEBCOOS_API_URL,
    apiVersion = 'v1',
    assetIdentifier,
    type,
    nameOrSlugPrefix,
    notNameOrSlugPrefix
}: Omit<IWebCOOSApiRequestParams, 'token' | 'signal'> & {
    assetIdentifier: string
    type?: 'image' | 'video'
    nameOrSlugPrefix?: string
    notNameOrSlugPrefix?: string
}): string => {
    const url = new URL(`${apiUrl}/${apiVersion}/assets/${assetIdentifier}/elements/first/${type ?? 'image'}`);
    if (nameOrSlugPrefix !== undefined) {
        url.searchParams.set('name_or_slug_prefix', nameOrSlugPrefix);
    } else if (notNameOrSlugPrefix !== undefined) {
        url.searchParams.set('not_name_or_slug_prefix', notNameOrSlugPrefix);
    }
    return url.toString();
}



export const latestAssetMediaEndpoint = ({
    apiUrl = import.meta.env.VITE_WEBCOOS_API_URL,
    apiVersion = 'v1',
    assetIdentifier,
    type,
    nameOrSlugPrefix,
    notNameOrSlugPrefix
}: Omit<IWebCOOSApiRequestParams, 'token' | 'signal'> & {
    assetIdentifier: string
    type?: 'image' | 'video'
    nameOrSlugPrefix?: string
    notNameOrSlugPrefix?: string
}): string => {
    const url = new URL(`${apiUrl}/${apiVersion}/assets/${assetIdentifier}/elements/latest/${type ?? 'image'}`);
    if (nameOrSlugPrefix !== undefined) {
        url.searchParams.set('name_or_slug_prefix', nameOrSlugPrefix);
    } else if (notNameOrSlugPrefix !== undefined) {
        url.searchParams.set('not_name_or_slug_prefix', notNameOrSlugPrefix);
    }
    return url.toString();
}


export const assetTimeSeriesEndpoint = ({
    apiUrl = import.meta.env.VITE_WEBCOOS_API_URL,
    apiVersion = 'v1',
    serviceIdentifier,
    start,
    end,
    page = 1,
    count = 5000,
    orderBy = 'starting',
    orderDir = 'desc',
    nameOrSlugPrefix,
    notNameOrSlugPrefix
}: Omit<IWebCOOSApiRequestParams, 'token' | 'signal'> & {
    serviceIdentifier: string
    start?: Date | string | number
    end?: Date | string | number
    page?: number
    count?: number
    orderBy?: string
    orderDir?: 'asc' | 'desc',
    nameOrSlugPrefix?: string
    notNameOrSlugPrefix?: string
}): string => {
    const url = new URL(`${apiUrl}/${apiVersion}/elements`)
    url.searchParams.set('service', serviceIdentifier);
    if (start !== undefined) {
        url.searchParams.set('starting_after', start instanceof Date ? start.toISOString() : makeUTCDate(start).toISOString());
    }
    if (end !== undefined) {
        url.searchParams.set('starting_before', end instanceof Date ? end.toISOString() : makeUTCDate(end).toISOString());
    }
    url.searchParams.set('page', page.toString());
    url.searchParams.set('page_size', count.toString());
    url.searchParams.set('ordering', `${orderDir === 'asc' ? '' : '-'}${orderBy}`);
    if (nameOrSlugPrefix !== undefined) {
        url.searchParams.set('name_or_slug_prefix', nameOrSlugPrefix);
    } else if (notNameOrSlugPrefix !== undefined) {
        url.searchParams.set('not_name_or_slug_prefix', notNameOrSlugPrefix);
    }
    return url.toString();
}

export const assetNextElementEndpoint = ({
    apiUrl = import.meta.env.VITE_WEBCOOS_API_URL,
    apiVersion = 'v1',
    serviceIdentifier,
    after,
    page = 1,
    count = 1,
    withinHours,
    nameOrSlugPrefix,
    notNameOrSlugPrefix
}: Omit<IWebCOOSApiRequestParams, 'token' | 'signal' | 'order' | 'orderBy'> & {
    serviceIdentifier: string
    after: Date | string | number
    page?: number
    count?: number
    withinHours?: number
    nameOrSlugPrefix?: string
    notNameOrSlugPrefix?: string
}): string => {
    const url = new URL(`${apiUrl}/${apiVersion}/elements`)
    url.searchParams.set('service', serviceIdentifier);
    const startingAfterDate = after instanceof Date ? after : makeUTCDate(after);
    url.searchParams.set('starting_after', startingAfterDate.toISOString());
    url.searchParams.set('page', page.toString());
    url.searchParams.set('page_size', count.toString());
    url.searchParams.set('ordering', 'starting');
    if (withinHours !== undefined) {
        url.searchParams.set('starting_before', new Date(startingAfterDate.getTime() + (withinHours * 60 * 60 * 1000)).toISOString());
    }
    if (nameOrSlugPrefix !== undefined) {
        url.searchParams.set('name_or_slug_prefix', nameOrSlugPrefix);
    } else if (notNameOrSlugPrefix !== undefined) {
        url.searchParams.set('not_name_or_slug_prefix', notNameOrSlugPrefix);
    }
    return url.toString();
}


export const assetPreviousElementEndpoint = ({
    apiUrl = import.meta.env.VITE_WEBCOOS_API_URL,
    apiVersion = 'v1',
    serviceIdentifier,
    before,
    page = 1,
    count = 1,
    withinHours,
    nameOrSlugPrefix,
    notNameOrSlugPrefix
}: Omit<IWebCOOSApiRequestParams, 'token' | 'signal' | 'order' | 'orderBy'> & {
    serviceIdentifier: string
    before: Date | string | number
    page?: number
    count?: number
    withinHours?: number
    nameOrSlugPrefix?: string
    notNameOrSlugPrefix?: string
}): string => {
    const url = new URL(`${apiUrl}/${apiVersion}/elements`)
    url.searchParams.set('service', serviceIdentifier);
    const startingBeforeDate = before instanceof Date ? before : makeUTCDate(before);
    url.searchParams.set('starting_before', startingBeforeDate.toISOString());
    if (withinHours !== undefined) {
        url.searchParams.set('starting_after', new Date(startingBeforeDate.getTime() - (withinHours * 60 * 60 * 1000)).toISOString());
    }
    url.searchParams.set('page', page.toString());
    url.searchParams.set('page_size', count.toString());
    url.searchParams.set('ordering', '-starting');
    if (nameOrSlugPrefix !== undefined) {
        url.searchParams.set('name_or_slug_prefix', nameOrSlugPrefix);
    } else if (notNameOrSlugPrefix !== undefined) {
        url.searchParams.set('not_name_or_slug_prefix', notNameOrSlugPrefix);
    }
    return url.toString();
}


export const firstServiceMediaEndpoint = ({
    apiUrl = import.meta.env.VITE_WEBCOOS_API_URL,
    apiVersion = 'v1',
    serviceIdentifier,
    nameOrSlugPrefix,
    notNameOrSlugPrefix
}: Omit<IWebCOOSApiRequestParams, 'token' | 'signal'> & {
    serviceIdentifier: string
    nameOrSlugPrefix?: string
    notNameOrSlugPrefix?: string
}): string => {
    const url = new URL(`${apiUrl}/${apiVersion}/services/${serviceIdentifier}/elements/first`)
    if (nameOrSlugPrefix !== undefined) {
        url.searchParams.set('name_or_slug_prefix', nameOrSlugPrefix);
    } else if (notNameOrSlugPrefix !== undefined) {
        url.searchParams.set('not_name_or_slug_prefix', notNameOrSlugPrefix);
    }
    return url.toString();
}


export const latestServiceMediaEndpoint = ({
    apiUrl = import.meta.env.VITE_WEBCOOS_API_URL,
    apiVersion = 'v1',
    serviceIdentifier,
    nameOrSlugPrefix,
    notNameOrSlugPrefix
}: Omit<IWebCOOSApiRequestParams, 'token' | 'signal'> & {
    serviceIdentifier: string
    nameOrSlugPrefix?: string
    notNameOrSlugPrefix?: string
}): string => {
    const url = new URL(`${apiUrl}/${apiVersion}/services/${serviceIdentifier}/elements/latest`);
    if (nameOrSlugPrefix !== undefined) {
        url.searchParams.set('name_or_slug_prefix', nameOrSlugPrefix);
    } else if (notNameOrSlugPrefix !== undefined) {
        url.searchParams.set('not_name_or_slug_prefix', notNameOrSlugPrefix);
    }
    return url.toString();
}



export const latestMediaRedirectEndpoint = ({
    apiUrl = import.meta.env.VITE_WEBCOOS_API_URL,
    apiVersion = 'v1',
    assetIdentifier,
    nameOrSlugPrefix,
    notNameOrSlugPrefix
}: Omit<IWebCOOSApiRequestParams, 'token' | 'signal'> & {
    assetIdentifier: string
    nameOrSlugPrefix?: string
    notNameOrSlugPrefix?: string
}): string => {
    const url = new URL(`${apiUrl}/${apiVersion}/elements/${assetIdentifier}/redirect`);
    if (nameOrSlugPrefix !== undefined) {
        url.searchParams.set('name_or_slug_prefix', nameOrSlugPrefix);
    } else if (notNameOrSlugPrefix !== undefined) {
        url.searchParams.set('not_name_or_slug_prefix', notNameOrSlugPrefix);
    }
    return url.toString();
}

export const elementMetadataEndpoint = ({
    apiUrl = import.meta.env.VITE_WEBCOOS_API_URL,
    apiVersion = 'v1',
    elementIdentifier
}: Omit<IWebCOOSApiRequestParams, 'token' | 'signal'> & {
    elementIdentifier: string
}): string => {
    const url = new URL(`${apiUrl}/${apiVersion}/elements/${elementIdentifier}/`);
    return url.toString()
}


export const serviceMetadataEndpoint = ({
    apiUrl = import.meta.env.VITE_WEBCOOS_API_URL,
    apiVersion = 'v1',
    serviceIdentifier
}: Omit<IWebCOOSApiRequestParams, 'token' | 'signal'> & {
    serviceIdentifier: string
}): string => {
    const url = new URL(`${apiUrl}/${apiVersion}/services/${serviceIdentifier}/`);
    return url.toString()
}